import ExcelJS from 'exceljs';

/**
 * Downloads JSON data as an Excel file (.xlsx) to the user's device with styling.
 *
 * @param jsonData - Array of objects to convert to Excel rows
 * @param fileName - Name of the file to download (should include .xlsx extension)
 * @param header - Optional array of column headers. If provided, determines column order and adds header row
 * @param sheetName - Optional name for the Excel sheet (defaults to 'Expenses')
 * @param originalStatementHeaders - Optional array of original statement column headers (to highlight in grey)
 */
export async function downloadExcelFile<T extends Record<string, unknown>>(
  jsonData: T[],
  fileName: string,
  header?: string[],
  sheetName: string = 'Expenses',
  originalStatementHeaders?: string[],
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  // Determine column order
  const columns = header || (jsonData.length > 0 && jsonData[0] ? Object.keys(jsonData[0]) : []);

  // Add header row
  if (header && header.length > 0) {
    worksheet.addRow(header);
  } else if (jsonData.length > 0 && jsonData[0]) {
    worksheet.addRow(columns);
  }

  // Add data rows with hyperlink support
  jsonData.forEach((row) => {
    const rowData: (string | number | { text: string; hyperlink: string })[] = [];
    columns.forEach((col) => {
      const rawValue = row[col];
      const value = rawValue ?? '';
      // Check if this is a URL column and the value is a valid URL
      const isUrlColumn =
        col === 'Expense URL*' ||
        col === 'Attachments*' ||
        col === 'Expense URL' ||
        col === 'Attachments';

      if (isUrlColumn && typeof value === 'string' && value.trim() !== '') {
        // Check if it's a valid URL
        try {
          const url = new URL(value);
          // Set as hyperlink
          rowData.push({
            text: value,
            hyperlink: url.toString(),
          });
        } catch {
          // Not a valid URL, just use the value as text
          rowData.push(value);
        }
      } else if (typeof value === 'string' || typeof value === 'number') {
        rowData.push(value);
      } else {
        // Convert other types to string
        rowData.push(String(value));
      }
    });
    worksheet.addRow(rowData);
  });

  // Apply styling and hyperlink formatting
  applyExcelStyling(worksheet, columns, originalStatementHeaders);

  // Generate buffer and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Applies styling to Excel worksheet cells
 */
function applyExcelStyling(
  worksheet: ExcelJS.Worksheet,
  columns: string[],
  originalStatementHeaders?: string[],
): void {
  if (columns.length === 0) {
    return;
  }

  // Style header row (first row)
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FF000000' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF5F5F5' },
  };
  headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
  headerRow.height = 20;

  // Apply borders to header row
  columns.forEach((_, colIndex) => {
    const cell = headerRow.getCell(colIndex + 1);
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    };
  });

  // Add autofilters to header row
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };

  // Style data rows
  worksheet.eachRow((row: ExcelJS.Row, rowNumber: number) => {
    if (rowNumber === 1) {
      return; // Skip header row
    }

    columns.forEach((columnName, colIndex) => {
      const cell = row.getCell(colIndex + 1);

      // Check if this is a URL column
      const isUrlColumn =
        columnName === 'Expense URL*' ||
        columnName === 'Attachments*' ||
        columnName === 'Expense URL' ||
        columnName === 'Attachments';

      // Check if this is an original statement column (to highlight in grey)
      const isOriginalStatementColumn =
        originalStatementHeaders && originalStatementHeaders.includes(columnName);

      // Check if this is the Status column
      const isStatusColumn = columnName === 'Status';

      // Determine if this is a numeric column
      const isNumericColumn =
        columnName.toLowerCase().includes('total') ||
        columnName.toLowerCase().includes('tax') ||
        columnName.toLowerCase().includes('subtotal') ||
        columnName.toLowerCase().includes('amount');

      // Apply borders
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      };

      // Apply alignment
      cell.alignment = {
        horizontal: isNumericColumn ? 'right' : 'left',
        vertical: 'middle',
      };

      // Apply conditional formatting to Status column (takes precedence)
      if (isStatusColumn && cell.value) {
        const statusValue = String(cell.value).toLowerCase();
        if (statusValue === 'reconciled') {
          // Green background for "Reconciled"
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE8F5E9' }, // Light green
          };
          cell.font = {
            color: { argb: 'FF2E7D32' }, // Dark green text
            bold: true,
          };
        } else if (statusValue === 'missing' || statusValue.includes('missing')) {
          // Red/Orange background for "Missing" or "Missing Receipt"
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFF3E0' }, // Light orange
          };
          cell.font = {
            color: { argb: 'FFE65100' }, // Dark orange text
            bold: true,
          };
        }
      } else if (isOriginalStatementColumn) {
        // Apply grey background to original statement columns (but not Status)
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF9F9F9' }, // Light grey background
        };
      }

      // Style hyperlinks with blue color and underline
      if (
        isUrlColumn &&
        cell.value &&
        typeof cell.value === 'object' &&
        'hyperlink' in cell.value
      ) {
        cell.font = {
          color: { argb: 'FF0000FF' }, // Blue color
          underline: true,
        };
      }
    });
  });

  // Set column widths
  columns.forEach((columnName, colIndex) => {
    const baseWidth = Math.max(columnName.length, 10);
    worksheet.getColumn(colIndex + 1).width = Math.min(baseWidth + 5, 50);
  });
}
